import type { SpaceDerivationInput } from '@toolproof-core/visualization/derivation/_contract/derivation';

import { createPrimitiveWorkflowSession } from '@/workflowExecution/createPrimitiveWorkflowSession';
import { requireConfig as requireRunnerConfig } from '@/workflow-runner/_pure/config/config';
import { runGraphAndForwardTrace } from '@/workflow/runtime/graphRuntime';
import { createMode as createBuilderMode, type Config as BuilderConfig } from '@/workflow-builder/mode';
import { workflowConceptAugmentation } from '@/workflow-runner/conceptArrangement';
import { withWorkflowSceneConfig } from '@/workflow-runner/episodeConfig';
import { episodePresentation as runnerEpisodePresentation } from '@/workflow-runner/episodePresentation';
import { mode as runnerMode, type Config as RunnerConfig } from '@/workflow-runner/mode';
import { DEFAULT_MODE_CONFIG } from '@/workflow/modes/_@shared/_data/configRegistry';
import { MODE_SECTION_ID } from '@/workflow/modes/_@shared/_data/modeOverlay';
import { selectEffectiveConfig } from '@/workflow/modes/_@shared/_pure/configSelection';
import {
    withModeOverlay,
} from '@/workflow/modes/_@shared/_pure/modeOverlay';
import { reduceModeIntent } from '@/workflow/modes/_@shared/modeSwitching';
import type {
    EpisodePresentation,
    ModeMappedControllerRegistry,
    SpaceAdapterModePolicy,
    SpaceAdapterRuntimeController,
} from '@/_contract/types';
import type { Config } from '@/workflow/_contract/config';
import type { CosmosDataModel } from '@/workflow/_contract/cosmosData';
import type { AdapterMode } from '@/workflow/_contract/mode';
import type { WorkflowDerivationInput, WorkflowModeAugmentationController } from '@/workflow/_contract/modeAugmentation';
import type { WorkflowRuntimeState } from '@/workflow/_contract/runtime';
import type {
    InteractiveRuntimeState,
    PipelineContext,
} from '@/workflow/modes/_@shared/_contract/mode';

const RUNNER_MODE_KEY = runnerMode.adapterMode;

type RuntimeController = SpaceAdapterRuntimeController<
    Config,
    InteractiveRuntimeState,
    CosmosDataModel
>;

const runnerModeControllerBase = runnerMode.runtimeController;
const builderMode = createBuilderMode({
    createLocalSession: createPrimitiveWorkflowSession,
    executeWorkflow: async (workflow, { writeLocalWorkflowTrace }) => {
        const result = await runGraphAndForwardTrace({
            request: { workflow, writeLocalWorkflowTrace },
            callbacks: {
                onParseError: (error) => {
                    console.warn('Failed to parse /api/run-graph NDJSON line:', error);
                },
            },
        });
        if (!result.ok) {
            console.error('[run-graph] run failed:', result.status, result.error ?? 'Unknown error');
        }
        return result;
    },
    handoffToRunner: ({ workflowTraceHandle, workflowTrace, updateRuntimeState, isCurrentRuntimeState }) => {
        updateRuntimeState((runtimeState: WorkflowRuntimeState) => {
            if (!isCurrentRuntimeState(runtimeState)) return runtimeState;
            const ephemeralWorkflowTraces = workflowTrace
                ? [workflowTrace, ...runtimeState.ephemeralWorkflowTraces ?? []]
                : runtimeState.ephemeralWorkflowTraces;
            return { ...runnerMode.createRuntimeStateForTrace({
                config: toRuntimeModeConfig(DEFAULT_CONFIG, RUNNER_MODE_KEY),
                workflowTraceHandle,
            }), ...(ephemeralWorkflowTraces ? { ephemeralWorkflowTraces } : {}) };
        });
    },
});
const BUILDER_MODE_KEY = builderMode.adapterMode;
const builderModeControllerBase = builderMode.runtimeController;

function toRuntimeModeConfig(config: Config, adapterMode: 'builder'): BuilderConfig;
function toRuntimeModeConfig(config: Config, adapterMode: 'runner'): RunnerConfig;
function toRuntimeModeConfig(config: Config, adapterMode: AdapterMode): BuilderConfig | RunnerConfig;
function toRuntimeModeConfig(
    config: Config,
    adapterMode: AdapterMode,
): BuilderConfig | RunnerConfig {
    const selectedConfig = selectEffectiveConfig(config, adapterMode);
    if (selectedConfig.adapterMode !== adapterMode) {
        throw new Error(`Workflow config selection returned '${selectedConfig.adapterMode}' for '${adapterMode}' mode.`);
    }
    return selectedConfig;
}

function createModeController(adapterMode: AdapterMode): RuntimeController {
    return {
        reducePlaybackSuspension: (args) => {
            if (args.runtimeState.adapterMode !== RUNNER_MODE_KEY || args.resumeState.adapterMode !== RUNNER_MODE_KEY) {
                return args.runtimeState;
            }
            return runnerModeControllerBase.reducePlaybackSuspension?.({
                ...args, runtimeState: args.runtimeState, resumeState: args.resumeState,
            }) ?? args.runtimeState;
        },
        createInitialRuntimeState: ({ config }) => adapterMode === BUILDER_MODE_KEY
            ? builderModeControllerBase.createInitialRuntimeState({
                config: toRuntimeModeConfig(config, BUILDER_MODE_KEY),
            })
            : runnerModeControllerBase.createInitialRuntimeState({
                config: toRuntimeModeConfig(config, RUNNER_MODE_KEY),
            }),
        buildOverlayModel: (args) => {
            if (args.runtimeState.adapterMode === BUILDER_MODE_KEY) {
                const overlayModel = builderModeControllerBase.buildOverlayModel?.({
                    ...args,
                    runtimeState: args.runtimeState,
                    config: toRuntimeModeConfig(args.config, BUILDER_MODE_KEY),
                });
                return withModeOverlay({ adapterMode: BUILDER_MODE_KEY, overlayModel });
            }
            const overlayModel = runnerModeControllerBase.buildOverlayModel?.({
                ...args,
                runtimeState: args.runtimeState,
                config: toRuntimeModeConfig(args.config, RUNNER_MODE_KEY),
            });
            return withModeOverlay({ adapterMode: RUNNER_MODE_KEY, overlayModel });
        },
        reduceOverlayIntent: (args) => {
            const modeState = reduceModeIntent<InteractiveRuntimeState>({
                runtimeState: args.runtimeState,
                intent: args.intent,
                createRuntimeStateByMode: {
                    builder: () => ({ ...builderModeControllerBase.createInitialRuntimeState({
                        config: toRuntimeModeConfig(args.config, BUILDER_MODE_KEY),
                    }), ...(args.runtimeState.ephemeralWorkflowTraces ? {
                        ephemeralWorkflowTraces: args.runtimeState.ephemeralWorkflowTraces,
                    } : {}) }),
                    runner: () => ({ ...runnerModeControllerBase.createInitialRuntimeState({
                        config: toRuntimeModeConfig(args.config, RUNNER_MODE_KEY),
                    }), ...(args.runtimeState.ephemeralWorkflowTraces ? {
                        ephemeralWorkflowTraces: args.runtimeState.ephemeralWorkflowTraces,
                    } : {}) }),
                },
            });
            if (modeState !== undefined) return modeState;

            if (args.runtimeState.adapterMode === BUILDER_MODE_KEY) {
                return builderModeControllerBase.reduceOverlayIntent?.({
                    ...args,
                    runtimeState: args.runtimeState,
                    config: toRuntimeModeConfig(args.config, BUILDER_MODE_KEY),
                }) ?? args.runtimeState;
            }

            return runnerModeControllerBase.reduceOverlayIntent?.({
                ...args,
                runtimeState: args.runtimeState,
                config: toRuntimeModeConfig(args.config, RUNNER_MODE_KEY),
            }) ?? args.runtimeState;
        },
        reduceDerivationTransitionComplete: (args) => {
            if (args.runtimeState.adapterMode === BUILDER_MODE_KEY) return args.runtimeState;
            return runnerModeControllerBase.reduceDerivationTransitionComplete?.({
                ...args,
                runtimeState: args.runtimeState,
                config: toRuntimeModeConfig(args.config, RUNNER_MODE_KEY),
            }) ?? args.runtimeState;
        },
        getRuntimeTickIntervalMs: (args) => {
            if (args.runtimeState.adapterMode === BUILDER_MODE_KEY) return undefined;
            return runnerModeControllerBase.getRuntimeTickIntervalMs?.({
                ...args,
                runtimeState: args.runtimeState,
                config: toRuntimeModeConfig(args.config, RUNNER_MODE_KEY),
            });
        },
        reduceRuntimeTick: (args) => {
            if (args.runtimeState.adapterMode === BUILDER_MODE_KEY) return args.runtimeState;
            return runnerModeControllerBase.reduceRuntimeTick?.({
                ...args,
                runtimeState: args.runtimeState,
                config: toRuntimeModeConfig(args.config, RUNNER_MODE_KEY),
            }) ?? args.runtimeState;
        },
    };
}

export const runnerModeController = createModeController(RUNNER_MODE_KEY);
export const builderModeController = createModeController(BUILDER_MODE_KEY);

type RegisteredModeKey = InteractiveRuntimeState['adapterMode'];

const MODE_CONTROLLERS = {
    [RUNNER_MODE_KEY]: runnerModeController,
    [BUILDER_MODE_KEY]: builderModeController,
} as const satisfies ModeMappedControllerRegistry<
    RegisteredModeKey,
    Config,
    InteractiveRuntimeState,
    CosmosDataModel
>;

export const MODE_POLICY = {
    defaultModeKey: RUNNER_MODE_KEY,
    modeControllers: MODE_CONTROLLERS,
    resolveModeKey: ({ config, runtimeState }) => runtimeState?.adapterMode ?? config.adapterMode,
} satisfies SpaceAdapterModePolicy<
    RegisteredModeKey,
    Config,
    InteractiveRuntimeState,
    CosmosDataModel
>;

const NO_INPUT_CONFIG = {};
const runtimeConfigBySource = new WeakMap<object, Map<string, Config>>();

export function resolveRuntimeConfig(
    input: SpaceDerivationInput<CosmosDataModel, Config, InteractiveRuntimeState>,
): Config {
    // Identity-stable per (config, mode) so identity-keyed derivation memos survive re-derivation.
    const source = input.config ?? NO_INPUT_CONFIG;
    let byMode = runtimeConfigBySource.get(source);
    if (!byMode) runtimeConfigBySource.set(source, byMode = new Map());
    let resolved = byMode.get(input.runtimeState.adapterMode);
    if (!resolved) {
        resolved = selectEffectiveConfig(input.config, input.runtimeState.adapterMode);
        byMode.set(input.runtimeState.adapterMode, resolved);
    }
    return resolved.adapterMode === RUNNER_MODE_KEY && input.runtimeState.adapterMode === RUNNER_MODE_KEY
        ? withWorkflowSceneConfig(requireRunnerConfig(resolved), input.runtimeState) : resolved;
}

export function createModeAugmentationResolver(): (
    input: WorkflowDerivationInput<Config, InteractiveRuntimeState, PipelineContext>,
    config: Config,
) => WorkflowModeAugmentationController<Config, InteractiveRuntimeState, PipelineContext> {
    const runnerAugmentationController = runnerMode.createAugmentationController();
    return (input, config) => input.runtimeState.adapterMode === RUNNER_MODE_KEY && input.runtimeState.conceptArrangement
        ? workflowConceptAugmentation : config.adapterMode === BUILDER_MODE_KEY
        ? builderMode.augmentationController
        : runnerAugmentationController;
}

export const DEFAULT_CONFIG: Config = {
    ...DEFAULT_MODE_CONFIG,
};

export const episodePresentation: EpisodePresentation<Config, InteractiveRuntimeState, CosmosDataModel> = {
    support: {
        ...runnerEpisodePresentation.support,
        trace: {
            ...runnerEpisodePresentation.support.trace,
            resolve: (args) => {
                if (args.runtimeState.adapterMode !== RUNNER_MODE_KEY) throw new Error('Workflow episode trace resolution requires Runner mode.');
                return runnerEpisodePresentation.support.trace.resolve({ ...args,
                    config: toRuntimeModeConfig(args.config, RUNNER_MODE_KEY), runtimeState: args.runtimeState });
            },
        },
    },
    episodes: runnerEpisodePresentation.episodes,
    adapterSectionId: MODE_SECTION_ID,
    canEnterEpisode: (state) => state.adapterMode === RUNNER_MODE_KEY,
    nextEpisode: (state, intent) => state.adapterMode === RUNNER_MODE_KEY
        ? runnerEpisodePresentation.nextEpisode?.(state, intent) : undefined,
    enter: (args) => runnerEpisodePresentation.enter({ ...args,
        config: toRuntimeModeConfig(args.config, RUNNER_MODE_KEY),
        runtimeState: args.runtimeState.adapterMode === RUNNER_MODE_KEY ? args.runtimeState
            : { ...runnerMode.runtimeController.createInitialRuntimeState({ config: toRuntimeModeConfig(args.config, RUNNER_MODE_KEY) }),
                ...(args.runtimeState.ephemeralWorkflowTraces ? { ephemeralWorkflowTraces: args.runtimeState.ephemeralWorkflowTraces } : {}) } }),
    detach: (state) => state.adapterMode === RUNNER_MODE_KEY ? runnerEpisodePresentation.detach(state) : state,
    playback: (state) => state.adapterMode === RUNNER_MODE_KEY ? runnerEpisodePresentation.playback(state) : undefined,
    inspectPlan: (state) => {
        if (state.adapterMode !== RUNNER_MODE_KEY) throw new Error('Workflow ScenePlan inspection requires Runner mode.');
        return runnerEpisodePresentation.inspectPlan(state);
    },
    pause: (state) => state.adapterMode === RUNNER_MODE_KEY ? runnerEpisodePresentation.pause(state) : state,
    settled: (state) => state.adapterMode !== RUNNER_MODE_KEY || runnerEpisodePresentation.settled(state),
    overlay: (model, guided) => runnerEpisodePresentation.overlay(model, guided),
};

export function createRuntimeEffectOwner() {
    const execute = builderMode.createExecutionOwner();
    const runnerEffects = runnerMode.createRuntimeEffectOwner();
    const synchronize = (args: { config: Config; runtimeState: InteractiveRuntimeState; cosmosData: CosmosDataModel;
        playbackActive?: boolean; animationSuspended?: boolean }) => {
        if (args.runtimeState.adapterMode === BUILDER_MODE_KEY) {
            runnerEffects.dispose();
            execute({ config: toRuntimeModeConfig(args.config, BUILDER_MODE_KEY), runtimeState: args.runtimeState });
        } else {
            runnerEffects({ ...args, config: toRuntimeModeConfig(args.config, RUNNER_MODE_KEY), runtimeState: args.runtimeState });
        }
    };
    return Object.assign(synchronize, { dispose: runnerEffects.dispose, spokenProgress: runnerEffects.spokenProgress });
}
